import React, { useEffect, useRef, useState } from 'react';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../firebase';

const FILTERS = [
    { id: 'normal', label: 'Original', css: 'none' },
    { id: 'mono', label: 'Mono', css: 'grayscale(1)' },
    { id: 'warm', label: 'Warm', css: 'sepia(0.35) saturate(1.25)' },
    { id: 'vivid', label: 'Vivid', css: 'contrast(1.15) saturate(1.4)' },
];

const ProfilePhotoSetup = ({ user, onComplete, onBack }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const [zoom, setZoom] = useState(1);
    const [offsetX, setOffsetX] = useState(0);
    const [offsetY, setOffsetY] = useState(0);
    const [filterId, setFilterId] = useState('normal');
    const [imageReady, setImageReady] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const canvasRef = useRef(null);
    const imageRef = useRef(null);

    useEffect(() => () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
    }, [previewUrl]);

    useEffect(() => {
        const image = imageRef.current;
        const canvas = canvasRef.current;
        if (!image || !canvas || !image.complete || !image.naturalWidth) return;

        const size = 720;
        const context = canvas.getContext('2d');
        const scale = Math.max(size / image.naturalWidth, size / image.naturalHeight) * zoom;
        const width = image.naturalWidth * scale;
        const height = image.naturalHeight * scale;
        const extraX = Math.max(0, width - size) / 2;
        const extraY = Math.max(0, height - size) / 2;

        context.clearRect(0, 0, size, size);
        context.filter = FILTERS.find((filter) => filter.id === filterId)?.css || 'none';
        context.drawImage(
            image,
            (size - width) / 2 + offsetX * extraX,
            (size - height) / 2 + offsetY * extraY,
            width,
            height
        );
        context.filter = 'none';
    }, [previewUrl, imageReady, zoom, offsetX, offsetY, filterId]);

    const handleFileChange = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            setError('Please choose an image file.');
            return;
        }
        if (file.size > 8 * 1024 * 1024) {
            setError('Image must be smaller than 8MB.');
            return;
        }
        setError('');
        setSelectedFile(file);
        setZoom(1);
        setOffsetX(0);
        setOffsetY(0);
        setFilterId('normal');
        setImageReady(false);
        setPreviewUrl(URL.createObjectURL(file));
    };

    const handleImageLoad = () => {
        setImageReady(true);
    };

    const handleSave = async () => {
        if (!selectedFile || !canvasRef.current || !user?.uid) {
            setError('Choose a profile photo first.');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const blob = await new Promise((resolve, reject) => {
                canvasRef.current.toBlob((result) => {
                    if (result) resolve(result);
                    else reject(new Error('Could not prepare the cropped image.'));
                }, 'image/jpeg', 0.9);
            });
            const imageRef = ref(storage, `avatars/${user.uid}/profile-${Date.now()}.jpg`);
            await uploadBytes(imageRef, blob, { contentType: 'image/jpeg' });
            const photoURL = await getDownloadURL(imageRef);
            await setDoc(doc(db, 'users', user.uid), {
                photoURL,
                photoUpdatedAt: serverTimestamp(),
            }, { merge: true });
            onComplete?.();
        } catch (uploadError) {
            console.error('Failed to save profile photo:', uploadError);
            setError(uploadError?.message || 'Could not save your profile photo.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0e0e0e] text-white flex items-center justify-center px-4 py-8">
            <div className="w-full max-w-md">
                <div className="text-center mb-6">
                    <div className="text-red-500 text-sm font-semibold tracking-[0.2em] uppercase">One last touch</div>
                    <h1 className="text-3xl font-bold mt-2">Add your profile photo</h1>
                    <p className="text-white/65 mt-2">Choose a photo that feels like you.</p>
                </div>

                {error && <div className="mb-4 rounded-lg border border-red-500/50 bg-red-500/10 p-3 text-sm text-red-200">{error}</div>}

                <div className="mx-auto aspect-square w-full max-w-[360px] overflow-hidden rounded-3xl bg-white/5 ring-1 ring-white/15">
                    {previewUrl ? (
                        <>
                            <img ref={imageRef} src={previewUrl} alt="Selected profile preview" className="hidden" onLoad={handleImageLoad} />
                            <canvas ref={canvasRef} width="720" height="720" className="h-full w-full" />
                        </>
                    ) : (
                        <div className="flex h-full flex-col items-center justify-center px-8 text-center text-white/45">
                            <div className="mb-3 text-6xl">📷</div>
                            <p>Select a photo to preview your square profile crop.</p>
                        </div>
                    )}
                </div>

                <label className="mt-5 block cursor-pointer rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-center font-semibold hover:bg-white/15">
                    {selectedFile ? 'Choose another photo' : 'Choose photo'}
                    <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
                </label>

                {selectedFile && (
                    <div className="mt-5 space-y-4 rounded-2xl bg-white/5 p-4 ring-1 ring-white/10">
                        <div>
                            <div className="mb-2 flex justify-between text-sm text-white/75"><span>Zoom</span><span>{zoom.toFixed(1)}x</span></div>
                            <input type="range" min="1" max="3" step="0.05" value={zoom} onChange={(event) => setZoom(Number(event.target.value))} className="w-full accent-red-500" />
                        </div>
                        <div>
                            <div className="mb-2 text-sm text-white/75">Move crop</div>
                            <input aria-label="Move crop horizontally" type="range" min="-1" max="1" step="0.01" value={offsetX} onChange={(event) => setOffsetX(Number(event.target.value))} className="w-full accent-red-500" />
                            <input aria-label="Move crop vertically" type="range" min="-1" max="1" step="0.01" value={offsetY} onChange={(event) => setOffsetY(Number(event.target.value))} className="mt-2 w-full accent-red-500" />
                        </div>
                        <div>
                            <div className="mb-2 text-sm text-white/75">Filter</div>
                            <div className="grid grid-cols-4 gap-2">
                                {FILTERS.map((filter) => (
                                    <button key={filter.id} type="button" onClick={() => setFilterId(filter.id)} className={`rounded-lg px-2 py-2 text-xs ${filterId === filter.id ? 'bg-red-500 text-white' : 'bg-white/10 text-white/75 hover:bg-white/15'}`}>
                                        {filter.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                <div className="mt-5 flex gap-3">
                    {onBack && <button type="button" onClick={onBack} disabled={loading} className="flex-1 rounded-xl bg-white/10 py-3 font-semibold hover:bg-white/15">Back</button>}
                    <button type="button" onClick={handleSave} disabled={loading || !selectedFile} className="flex-1 rounded-xl bg-gradient-to-br from-[#ff3b3b] to-[#ff0000] py-3 font-semibold disabled:cursor-not-allowed disabled:opacity-50">
                        {loading ? 'Saving...' : 'Save photo'}
                    </button>
                </div>
                <button type="button" onClick={() => onComplete?.()} disabled={loading} className="mt-3 w-full py-2 text-sm text-white/55 hover:text-white">Skip for now</button>
            </div>
        </div>
    );
};

export default ProfilePhotoSetup;
